<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell *ngIf="!isLoading && loadError">
    <p role="alert">{{ loadError }}</p>
    <p-button label="Back to Drivers" icon="pi pi-arrow-left" (onClick)="backToDrivers()"></p-button>
</app-page-shell>

<app-page-shell *ngIf="!isLoading && !loadError">

    <app-page-detail-hero
        eyebrow="Driver"
        [title]="driver.name || ('Driver # ' + driverId)"
        [subtitle]="driver.phoneNumber || driver.emailAddress || 'Driver profile'"
        icon="fa-solid fa-user-tie">
        <div tags>
            <p-tag [value]="driver.isPhoneVerified ? 'Phone verified' : 'Phone pending'" [severity]="driver.isPhoneVerified ? 'success' : 'warn'"></p-tag>
            <p-tag [value]="driver.currentVehicle?.plateNumber || 'No vehicle assigned'" severity="info"></p-tag>
            <p-tag *ngIf="driver.resourceCompanyName" [value]="driver.resourceCompanyName" severity="secondary"></p-tag>
        </div>
        <div actions>
            <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (onClick)="backToDrivers()"></p-button>
            <p-button *ngIf="!isReadOnlyResource" label="Edit" icon="pi pi-pencil" (onClick)="editDriver()"></p-button>
            <p-button *ngIf="!isReadOnlyResource" label="Change ownership" icon="pi pi-sync" severity="warn" [outlined]="true" (onClick)="changeOwnership()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="driverMetrics()"></app-page-metric-strip>

    <div class="detail-layout">
        <app-page-data-panel title="Driver Profile" subtitle="Identity, contact, and operating address.">
            <div class="profile-header">
                <img [src]="driver.imageUrl || 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'" [alt]="driver.name || 'Driver'">
                <div>
                    <strong>{{ driver.name || '-' }}</strong>
                    <span>{{ driver.emailAddress || '-' }}</span>
                </div>
            </div>

            <dl class="detail-grid">
                <div><dt>Phone</dt><dd>{{ driver.phoneNumber || '-' }}</dd></div>
                <div><dt>Source Company</dt><dd>{{ driver.resourceCompanyName || '-' }}</dd></div>
                <div><dt>Nationality</dt><dd>{{ driver.nationality?.name || '-' }}</dd></div>
                <div><dt>Residing Country</dt><dd>{{ driver.residingCountry?.name || '-' }}</dd></div>
                <div><dt>Home Location</dt><dd>{{ driver.homeLocation?.name || '-' }}</dd></div>
                <div><dt>Home Coordinates</dt><dd>{{ formatHomeLocationCoordinates() }}</dd></div>
                <div><dt>Preferred Address</dt><dd>{{ driver.preferredAddress || '-' }}</dd></div>
                <div><dt>Iqama Number</dt><dd>{{ driver.residenceNumber || '-' }}</dd></div>
                <div><dt>Iqama Expiry</dt><dd>{{ driver.residenceExpiryDate | date:'dd/MM/yyyy' }}</dd></div>
                <div><dt>License Number</dt><dd>{{ driver.licenseNumber || '-' }}</dd></div>
                <div><dt>License Expiry</dt><dd>{{ driver.licenseExpiryDate | date:'dd/MM/yyyy' }}</dd></div>
            </dl>
        </app-page-data-panel>

        <app-page-data-panel title="Documents & Assignment" subtitle="Linked vehicle and uploaded identity documents.">
            <dl class="detail-grid">
                <div><dt>Current Vehicle</dt><dd>{{ driver.currentVehicle?.plateNumber || '-' }}</dd></div>
                <div><dt>Vehicle Registration</dt><dd>{{ driver.currentVehicle?.registrationNumber || '-' }}</dd></div>
                <div><dt>Vehicle Type</dt><dd>{{ driver.currentVehicle?.type?.name || '-' }}</dd></div>
                <div><dt>Vehicle Capacity</dt><dd>{{ driver.currentVehicle?.capacity?.name || '-' }}</dd></div>
            </dl>

            <div class="document-links">
                <a *ngIf="documentUrl(driver.residenceImage)" [href]="driver.residenceImage" target="_blank">Residence Document</a>
                <a *ngIf="documentUrl(driver.licenseImage)" [href]="driver.licenseImage" target="_blank">License Document</a>
                <span *ngIf="!driver.residenceImage && !driver.licenseImage">No documents uploaded.</span>
            </div>
        </app-page-data-panel>

        <app-page-data-panel *ngIf="!isReadOnlyResource" title="Workforce Finance" subtitle="Cash accountability and work earnings for this driver across every linked workforce role.">
            <div class="cash-grid">
                <div><span>Field Person</span><strong>{{ workforcePerson?.displayName || 'Not linked' }}</strong><small>{{ workforcePerson?.roles?.join(' · ') || 'Add this driver to Field People' }}</small></div>
                <div><span>Cash accounts</span><strong>{{ workforceCashAccounts.length }}</strong><small>By currency; balances are never combined</small></div>
                <div><span>Earnings awaiting review</span><strong>{{ workforceEarnings.length }}</strong><small>Distance, loading and unloading work</small></div>
                <div><span>Finance status</span><strong>{{ workforcePerson?.status === 'ACTIVE' ? 'Active' : 'Needs review' }}</strong><small>Cash and payouts remain separate</small></div>
            </div>
            <div class="cash-actions">
                <p-button label="Open Workforce Finance" icon="pi pi-external-link" [routerLink]="['/accounts/enterprise/workforce-finance']" [queryParams]="{ tab: 'BALANCES', fieldWorkerId: workforcePerson?.fieldWorkerId }"></p-button>
                <p-button label="View earnings" icon="pi pi-money-bill" [outlined]="true" [routerLink]="['/accounts/enterprise/workforce-finance']" [queryParams]="{ tab: 'EARNINGS', fieldWorkerId: workforcePerson?.fieldWorkerId }"></p-button>
            </div>
            <p-table [value]="workforceCashAccounts" styleClass="p-datatable-sm">
                <ng-template pTemplate="header"><tr><th>Currency</th><th>Cash balance</th><th>Status</th><th>Last activity</th></tr></ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>{{ row.currencyCode }}</td>
                        <td>{{ row.currentBalance | number:'1.2-2' }}</td>
                        <td>{{ row.status }}</td>
                        <td>{{ row.lastTransactionAt | date:'short' }}</td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage"><tr><td colspan="4">No Workforce Finance cash account found for this driver.</td></tr></ng-template>
            </p-table>
        </app-page-data-panel>

        <app-page-data-panel title="Ownership correction history" subtitle="Immutable record of operating-ownership corrections.">
            <p-table [value]="ownershipHistory" styleClass="p-datatable-sm">
                <ng-template pTemplate="header"><tr><th>When</th><th>Change</th><th>Reason</th><th>Outcome</th></tr></ng-template>
                <ng-template pTemplate="body" let-row><tr><td>{{ row.createdAt | date:'short' }}</td><td><span *ngIf="row.targetCompanyId && row.targetCompanyId !== row.owningCompanyId">Company #{{ row.owningCompanyId }} → Company #{{ row.targetCompanyId }} · </span>{{ row.sourceOwnershipType }} → {{ row.targetOwnershipType }}</td><td>{{ row.reason }}</td><td>{{ row.outcome }}</td></tr></ng-template>
                <ng-template pTemplate="emptymessage"><tr><td colspan="4">No ownership corrections recorded.</td></tr></ng-template>
            </p-table>
        </app-page-data-panel>
    </div>
</app-page-shell>

<app-fleet-ownership-transfer-dialog [(visible)]="ownershipTransferVisible" [owningCompanyId]="currentCompanyId"
    [resources]="ownershipTransferResources" [pairedResource]="ownershipTransferPairedResource"
    (completed)="onOwnershipTransferCompleted($event)"></app-fleet-ownership-transfer-dialog>
